/**
 * HubSpot tracking helpers for the public Culture Trust Center.
 * Campaign links: https://platform.kincode.app/trust-center/{publicId}?email={{contact.email}}
 */

const SESSION_EMAIL_KEY = 'kincode_trust_center_prospect_email';

/** Short event name; JS API uses {@link trustCenterViewedEventName}. Matches HubSpot `pe{portalId}_trust_center`. */
export const HUBSPOT_EVENT_TRUST_CENTER_VIEWED = 'trust_center';

/** Human-readable page title sent on every custom event (timeline / workflows). */
export const TRUST_CENTER_EVENT_TITLE = 'Trust Center';

/**
 * Base properties for trust_center (always includes title "Trust Center").
 * @param {Record<string, string | number | boolean>} [extra]
 * @returns {Record<string, string | number | boolean>}
 */
export function trustCenterViewedProperties(extra = {}) {
  return {
    title: TRUST_CENTER_EVENT_TITLE,
    ...extra,
  };
}

/**
 * HubSpot fully-qualified custom behavioral event name.
 * @param {string} portalId
 * @returns {string}
 */
export function trustCenterViewedEventName(portalId) {
  const id = typeof portalId === 'string' ? portalId.trim() : '';
  if (!id) return HUBSPOT_EVENT_TRUST_CENTER_VIEWED;
  return `pe${id}_${HUBSPOT_EVENT_TRUST_CENTER_VIEWED}`;
}

/** Basic email shape check (HubSpot also validates on their side). */
export function isValidEmail(value) {
  if (typeof value !== 'string') return false;
  const email = value.trim();
  if (email.length < 3 || email.length > 254) return false;
  if (/[<>\s]/.test(email)) return false;
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
}

/**
 * @param {string} [search] - defaults to window.location.search
 * @returns {string | null}
 */
export function readEmailFromSearch(
  search = typeof window !== 'undefined' ? window.location.search : ''
) {
  try {
    const params = new URLSearchParams(search.startsWith('?') ? search : `?${search}`);
    const raw = params.get('email');
    if (!raw) return null;
    const decoded = decodeURIComponent(raw).trim();
    return isValidEmail(decoded) ? decoded.toLowerCase() : null;
  } catch {
    return null;
  }
}

/**
 * Remove `email` from the address bar without a navigation reload.
 */
export function stripEmailFromUrl() {
  if (typeof window === 'undefined') return;
  try {
    const url = new URL(window.location.href);
    if (!url.searchParams.has('email')) return;
    url.searchParams.delete('email');
    const next = `${url.pathname}${url.search}${url.hash}`;
    window.history.replaceState({}, '', next);
  } catch {
    // ignore
  }
}

/**
 * @param {string} email
 */
export function persistProspectEmail(email) {
  if (typeof sessionStorage === 'undefined' || !isValidEmail(email)) return;
  try {
    sessionStorage.setItem(SESSION_EMAIL_KEY, email.trim().toLowerCase());
  } catch {
    // ignore quota / private mode
  }
}

/** @returns {string | null} */
export function getPersistedProspectEmail() {
  if (typeof sessionStorage === 'undefined') return null;
  try {
    const email = sessionStorage.getItem(SESSION_EMAIL_KEY);
    return isValidEmail(email) ? email : null;
  } catch {
    return null;
  }
}

/** @returns {unknown[]} */
function getHsq() {
  if (typeof window === 'undefined') return [];
  window._hsq = window._hsq || [];
  return window._hsq;
}

/**
 * Load HubSpot **analytics-only** script once.
 * Uses `hs-analytics` (not the full `hs-scripts` loader) so Trust Center gets
 * identify + custom events without HubSpot chat, banners, or ads pixels.
 * @param {string} portalId
 */
export function loadHubSpotTrackingScript(portalId) {
  if (typeof document === 'undefined' || !portalId) return;
  const id = 'hs-trust-center-script-loader';
  if (document.getElementById(id)) return;

  // Ensure the queue exists before the analytics script boots.
  window._hsq = window._hsq || [];
  // Belt-and-suspenders if anything else tries to boot Conversations on this page.
  window.hsConversationsSettings = {
    ...(window.hsConversationsSettings || {}),
    loadImmediately: false,
  };

  const script = document.createElement('script');
  script.id = id;
  script.type = 'text/javascript';
  script.async = true;
  script.defer = true;
  // Cache-bust path segment matches HubSpot’s own loader pattern.
  script.src = `https://js-eu1.hs-analytics.net/analytics/${Date.now()}/${portalId}.js`;
  document.head.appendChild(script);
}

/**
 * Identify visitor by email, then track a page view so HubSpot receives the identity.
 * @param {string} email
 * @returns {boolean}
 */
export function identifyProspect(email) {
  if (!isValidEmail(email)) return false;
  const normalized = email.trim().toLowerCase();
  const _hsq = getHsq();
  _hsq.push(['identify', { email: normalized }]);
  _hsq.push(['trackPageView']);
  persistProspectEmail(normalized);
  return true;
}

/**
 * Custom behavioral event (must be defined in HubSpot for reporting / workflows).
 * @param {string} eventName - fully-qualified name when possible
 * @param {Record<string, string | number | boolean>} [properties]
 */
export function trackTrustCenterEvent(eventName, properties = {}) {
  if (typeof eventName !== 'string' || !eventName) return;
  const _hsq = getHsq();
  _hsq.push([
    'trackCustomBehavioralEvent',
    {
      name: eventName,
      properties,
    },
  ]);
}

/**
 * Boot HubSpot for Trust Center: load script, identify from ?email=, strip query param.
 * @param {{ portalId?: string, publicId?: string | null }} [options]
 * @returns {{ email: string | null, identified: boolean, portalId: string }}
 */
export function initHubSpotTrustCenterTracking(options = {}) {
  const portalId =
    options.portalId !== undefined && options.portalId !== null
      ? String(options.portalId)
      : import.meta.env.VITE_HUBSPOT_PORTAL_ID || '';

  const publicId =
    typeof options.publicId === 'string' && options.publicId.trim()
      ? options.publicId.trim()
      : null;

  if (!portalId) {
    if (import.meta.env.DEV) {
      console.warn(
        '[hubspot] VITE_HUBSPOT_PORTAL_ID is not set — Trust Center HubSpot tracking skipped'
      );
    }
    const emailFromUrl = readEmailFromSearch();
    if (emailFromUrl) {
      persistProspectEmail(emailFromUrl);
      stripEmailFromUrl();
    }
    return { email: getPersistedProspectEmail(), identified: false, portalId: '' };
  }

  loadHubSpotTrackingScript(portalId);

  const emailFromUrl = readEmailFromSearch();
  const email = emailFromUrl || getPersistedProspectEmail();
  let identified = false;

  if (emailFromUrl) {
    identified = identifyProspect(emailFromUrl);
    stripEmailFromUrl();
  } else if (email) {
    identified = identifyProspect(email);
  } else {
    getHsq().push(['trackPageView']);
  }

  if (publicId) {
    trackTrustCenterEvent(
      trustCenterViewedEventName(portalId),
      trustCenterViewedProperties({ publicId })
    );
  }

  return { email: getPersistedProspectEmail(), identified, portalId };
}
